import { getBreadcrumbParentsFromState } from './breadcrumbNavigation';

/** @typedef {{ name: string, link?: string, state?: object, icon?: string }} BreadcrumbItem */

const PROPERTY_TAB_LABELS = {
  report: 'Report',
  applications: 'Applications',
  policy: 'Policy',
  claim: 'Claim',
  invoice: 'Invoice',
  details: 'Details',
  'flagged-cancellation': 'Flagged Cancellation',
};

const LANDLORD_TAB_LABELS = {
  report: 'Report',
  properties: 'Properties',
  applications: 'Applications',
  policy: 'Policy',
  claim: 'Claim',
  invoice: 'Invoice',
};

const USER_ROLE_CONFIG = {
  'full-portfolio': { section: 'Full Portfolio', listLink: '/user/full-portfolio' },
  'multi-site': { section: 'Multi-Site', listLink: '/user/multi-site' },
  property: { section: 'Property Manager', listLink: '/user/property' },
};

const INVOICE_TAB_LABELS = {
  current: 'Current',
  'dispute-history': 'Dispute History',
};

/**
 * @param {string} pathname
 * @param {import('react-router').Location['state']} locationState
 * @param {Record<string, string|undefined|null>} labels
 * @returns {BreadcrumbItem[]}
 */
export function buildBreadcrumbsFromRoute(pathname, locationState, labels = {}) {
  if (!pathname) return [];

  const parentsFromState = getBreadcrumbParentsFromState(locationState);
  /** @type {BreadcrumbItem[]} */
  const crumbs = [];

  const push = (item) => {
    if (item?.name) crumbs.push(item);
  };

  const pushParents = () => {
    parentsFromState.forEach((p) => {
      if (p?.name && p?.link) {
        push({ name: p.name, link: p.link, state: p.state });
      }
    });
  };

  // Portfolio > Properties > {building} > {tab?}
  let match = pathname.match(/^\/property\/properties\/([^/]+)\/detail(?:\/([^/]+))?$/);
  if (match) {
    const tab = match[2];
    push({ name: 'Portfolio' });
    push({ name: 'Properties', link: '/property/properties' });
    pushParents();
    if (labels.buildingName) {
      push({
        name: labels.buildingName,
        link: tab ? `/property/properties/${match[1]}/detail` : undefined,
      });
    }
    if (tab && PROPERTY_TAB_LABELS[tab]) {
      push({ name: PROPERTY_TAB_LABELS[tab] });
    }
    return crumbs;
  }

  // Legacy buildings path
  match = pathname.match(/^\/property\/buildings\/([^/]+)\/detail(?:\/([^/]+))?$/);
  if (match) {
    push({ name: 'Portfolio' });
    push({ name: 'Properties', link: '/property/properties' });
    pushParents();
    if (labels.buildingName) {
      push({ name: labels.buildingName, link: `/property/properties/${match[1]}/detail` });
    }
    return crumbs;
  }

  match = pathname.match(/^\/property\/applications\/([^/]+)\/detail$/);
  if (match) {
    push({ name: 'Portfolio' });
    push({ name: 'Applications', link: '/property/applications' });
    pushParents();
    if (labels.applicationName) push({ name: labels.applicationName });
    return crumbs;
  }

  match = pathname.match(/^\/property\/policies\/([^/]+)\/detail$/);
  if (match) {
    push({ name: 'Portfolio' });
    push({ name: 'Policies', link: '/property/policies' });
    pushParents();
    if (labels.policyName) push({ name: labels.policyName });
    return crumbs;
  }

  match = pathname.match(/^\/property\/claims\/([^/]+)\/detail$/);
  if (match) {
    push({ name: 'Portfolio' });
    push({ name: 'Claims', link: '/property/claims' });
    pushParents();
    if (labels.claimName) push({ name: labels.claimName });
    return crumbs;
  }

  match = pathname.match(/^\/property\/cancel-policies\/([^/]+)\/detail$/);
  if (match) {
    push({ name: 'Portfolio' });
    push({ name: 'Flagged Cancellations', link: '/property/cancel-policies' });
    pushParents();
    if (labels.flaggedCancellationName) push({ name: labels.flaggedCancellationName });
    return crumbs;
  }

  // User > Operator > {operator} > {tab?} (legacy /user/company paths still match)
  match = pathname.match(/^\/user\/(?:operator|company)\/([^/]+)\/detail(?:\/([^/]+))?$/);
  if (match) {
    const tab = match[2];
    push({ name: 'Users', link: '/user/operator' });
    push({ name: 'Operator', link: '/user/operator' });
    pushParents();
    if (labels.landlordName) {
      push({
        name: labels.landlordName,
        link: tab ? `/user/operator/${match[1]}/detail` : undefined,
      });
    }
    if (tab && LANDLORD_TAB_LABELS[tab]) {
      push({ name: LANDLORD_TAB_LABELS[tab] });
    }
    return crumbs;
  }

  // User role nested building: /user/full-portfolio/detail/buildingDetail/:id/:tab?
  match = pathname.match(
    /^\/user\/(full-portfolio|multi-site|property)\/detail\/(?:propertyDetail|buildingDetail)\/([^/]+)(?:\/([^/]+))?$/,
  );
  if (match) {
    const roleKey = match[1];
    const tab = match[3];
    const config = USER_ROLE_CONFIG[roleKey];
    push({ name: 'Users', link: config?.listLink });
    push({ name: config?.section, link: config?.listLink });
    pushParents();
    if (labels.buildingName) {
      push({
        name: labels.buildingName,
        link: tab ? `/user/${roleKey}/detail/buildingDetail/${match[2]}` : undefined,
      });
    }
    if (tab && PROPERTY_TAB_LABELS[tab]) {
      push({ name: PROPERTY_TAB_LABELS[tab] });
    }
    return crumbs;
  }

  // User role delegation detail: /user/full-portfolio/:delegationId/detail
  match = pathname.match(/^\/user\/(full-portfolio|multi-site|property)\/([^/]+)\/detail$/);
  if (match) {
    const config = USER_ROLE_CONFIG[match[1]];
    push({ name: 'Users', link: config?.listLink });
    push({ name: config?.section, link: config?.listLink });
    pushParents();
    if (labels.userName) push({ name: labels.userName });
    return crumbs;
  }

  match = pathname.match(/^\/invoice\/([^/]+)\/detail(?:\/([^/]+))?$/);
  if (match) {
    const tab = match[2];
    push({ name: 'Invoice', link: '/invoice' });
    pushParents();
    if (labels.invoiceLabel) {
      push({
        name: labels.invoiceLabel,
        link: tab ? `/invoice/${match[1]}/detail` : undefined,
      });
    }
    if (tab && INVOICE_TAB_LABELS[tab]) {
      push({ name: INVOICE_TAB_LABELS[tab] });
    }
    return crumbs;
  }

  match = pathname.match(
    /^\/(?:invitations|email)\/([^/]+)\/detail(?:\/(?:propertyDetail|buildingDetail)\/([^/]+)(?:\/([^/]+))?)?$/,
  );
  if (match) {
    const listLink = pathname.startsWith('/email') ? '/email' : '/invitations';
    const tab = match[3];
    push({ name: 'Add Users', link: listLink });
    push({ name: 'Invitations', link: listLink });
    pushParents();
    if (match[2] && labels.buildingName) {
      push({
        name: labels.buildingName,
        link: tab ? `${listLink}/${match[1]}/detail/buildingDetail/${match[2]}` : undefined,
      });
    } else if (labels.emailLabel) {
      push({ name: labels.emailLabel });
    }
    if (tab && PROPERTY_TAB_LABELS[tab]) {
      push({ name: PROPERTY_TAB_LABELS[tab] });
    }
    return crumbs;
  }

  match = pathname.match(/^\/(?:invitations|email)\/([^/]+)\/detail$/);
  if (match) {
    const listLink = pathname.startsWith('/email') ? '/email' : '/invitations';
    push({ name: 'Add Users', link: listLink });
    push({ name: 'Invitations', link: listLink });
    pushParents();
    if (labels.emailLabel) push({ name: labels.emailLabel });
    return crumbs;
  }

  match = pathname.match(/^\/notification\/(received|outgoing)\/([^/]+)\/detail$/);
  if (match) {
    const isOutgoing = match[1] === 'outgoing';
    push({ name: 'Add Users', link: '/notification' });
    push({
      name: isOutgoing ? 'Notification' : 'Notification',
      link: isOutgoing ? '/notification?tab=outgoing' : '/notification?tab=received',
    });
    pushParents();
    if (labels.notificationLabel) push({ name: labels.notificationLabel });
    return crumbs;
  }

  if (pathname === '/invite') {
    push({ name: 'Add Users', link: '/invitations' });
    push({ name: 'Invite New User', link: '/invite' });
    return crumbs;
  }

  match = pathname.match(/^\/(?:operator|company)-invite\/([^/]+)\/detail(?:\/([^/]+))?$/);
  if (match) {
    push({ name: 'Add Users', link: '/operator-invite' });
    push({ name: 'Operator Invite', link: '/operator-invite' });
    pushParents();
    if (labels.landlordName) push({ name: labels.landlordName });
    return crumbs;
  }

  match = pathname.match(/^\/user\/invite-new-user\/([^/]+)\/detail(?:\/([^/]+))?$/);
  if (match) {
    push({ name: 'Add Users', link: '/user/invite-new-user' });
    push({ name: 'Invite New User', link: '/user/invite-new-user' });
    pushParents();
    if (labels.landlordName) push({ name: labels.landlordName });
    return crumbs;
  }

  return crumbs;
}

/**
 * Fallback crumbs from sidebar menu when route is a list page.
 * @param {Array<{ name: string, link?: string, icon?: string }>} menuItems
 */
export function buildMenuBreadcrumbs(menuItems) {
  if (!Array.isArray(menuItems)) return [];
  return menuItems
    .filter((item) => item?.name)
    .map((item, index, arr) => ({
      name: item.name,
      link: item.link || undefined,
      icon: item.icon,
      ...(index === arr.length - 1 ? {} : {}),
    }));
}
